import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const SESSION_KEY = "bb_intro_seen";

/**
 * Terminal-style intro: a black screen with the brand wordmark centered, shown
 * once per browser session on first load, then fades out to reveal the site.
 * Skippable (click / any key), respects reduced motion, and locks scroll while
 * visible. Decorative only — the page content is already in the DOM behind it.
 */
export function IntroSplash() {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return !sessionStorage.getItem(SESSION_KEY);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!show) return;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* private mode — just show it this once */
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dismiss = () => setShow(false);
    // Hold long enough for the 3.2s assembly clip plus a beat on the brand.
    const timer = window.setTimeout(dismiss, reduce ? 1500 : 4200);
    window.addEventListener("keydown", dismiss);
    window.addEventListener("pointerdown", dismiss);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("pointerdown", dismiss);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, reduce]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 1.1, delay: reduce ? 0 : 0.6, ease: [0.16, 1, 0.3, 1] } }}
          aria-hidden="true"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_45%,rgba(125,255,166,0.08),transparent_70%)]" />
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10, filter: "blur(8px)" }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={
              reduce
                ? { opacity: 0 }
                : { opacity: 0, scale: 1.14, filter: "blur(16px)", transition: { duration: 1.5, ease: [0.22, 1, 0.36, 1] } }
            }
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative px-6 text-center"
          >
            {/* Logo assembly clip (rendered offline, like the hero video): the
                bear slides in from the left, the bull charges in from the right
                with motion-blur trails, they lock into the finished logo with an
                impact flash. The clip's black background blends into the splash.
                Reduced motion: static final-frame poster instead of the video. */}
            <div className="relative mx-auto mb-4 w-[clamp(220px,44vw,380px)]">
              {reduce ? (
                <img src="/brand/splash-poster.jpg" alt="" className="w-full" draggable={false} />
              ) : (
                <video
                  src="/brand/splash-intro.mp4"
                  poster="/brand/splash-poster.jpg"
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                  className="w-full"
                />
              )}
              {/* Feathered edges so the clip melts into the black screen */}
              <div className="pointer-events-none absolute inset-0" style={{ boxShadow: "inset 0 0 44px 44px #000" }} />
            </div>

            {/* Wordmark appears once the animals have met, with a one-time light sweep */}
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: reduce ? 0.2 : 1.25, ease: [0.16, 1, 0.3, 1] }}
              className="relative text-[clamp(2.25rem,9vw,5.5rem)] font-bold leading-none tracking-tight text-white"
            >
              BULL <span className="text-accent-500">&amp;</span> BEAR CO.
              {!reduce && (
                <motion.span
                  aria-hidden="true"
                  initial={{ backgroundPosition: "-180% 0" }}
                  animate={{ backgroundPosition: "280% 0" }}
                  transition={{ duration: 1.6, delay: 1.5, ease: "easeInOut" }}
                  className="pointer-events-none absolute inset-0 bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.85) 50%, transparent 58%)",
                    backgroundSize: "60% 100%",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  BULL <span>&amp;</span> BEAR CO.
                </motion.span>
              )}
            </motion.div>

            {/* Highway center-line: dashes drift past like road markings under a truck */}
            <motion.div
              initial={reduce ? { opacity: 1 } : { opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.7, delay: reduce ? 0.3 : 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto mt-6 h-[3px] w-56 origin-center overflow-hidden"
              style={{
                maskImage: "linear-gradient(to right, transparent, black 25%, black 75%, transparent)",
                WebkitMaskImage: "linear-gradient(to right, transparent, black 25%, black 75%, transparent)",
              }}
            >
              <motion.div
                animate={reduce ? undefined : { backgroundPosition: ["0px 0", "-84px 0"] }}
                transition={{ duration: 1.4, ease: "linear", repeat: Infinity }}
                className="h-full w-full"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(90deg, rgba(125,255,166,0.9) 0 16px, transparent 16px 42px)",
                }}
              />
            </motion.div>

            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, letterSpacing: "0.7em" }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, letterSpacing: "0.45em" }}
              transition={{ duration: 0.9, delay: reduce ? 0.4 : 1.55, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 text-[0.7rem] uppercase tracking-[0.45em] text-white/55 sm:text-xs"
            >
              Asset-Based Carrier
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
